// src/features/people/PersonProfile.jsx - CON FUNCIONALIDAD SHOWDELETED
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Tabs, Tab, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaEdit, FaArrowLeft, FaUserMinus, FaCloudUploadAlt, FaUndo, FaLock, FaEye } from 'react-icons/fa';
import dayjs from 'dayjs';

// Servicios
import PeopleService from './PeopleService';
import { useNavigationHistory } from '../../hooks/useNavigationHistory';

// Componentes
import CustomSnackbar from '../../components/CustomSnackbar';
import DocumentsSection from './components/DocumentsSection';
import WorksSection from './components/WorksSection';
import ToolsSection from './components/ToolsSection';
import TrainingsSection from './components/TrainingsSection';
import InfoRow from './components/InfoRow';

// Constantes y funciones helper
import { TIPOS_GENERO, PAISES, FORMATO_FECHA } from '../../utils/constants';
import { getDescripcionGenero, getDescripcionPais } from '../../utils/constants';

// Estilos
import '../../features/people/PersonProfile.css';

function PersonProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [activeTab, setActiveTab] = useState('documentos');

  // ✅ useRef para prevenir múltiples llamadas
  const loadingRef = useRef(false);
  const loadedPersonRef = useRef(null);

  const { goBack } = useNavigationHistory();

  // ✅ Función memoizada para cargar datos - OPTIMIZADA
  const loadPersonData = useCallback(async () => {
    // ✅ Prevenir múltiples llamadas simultáneas
    if (loadingRef.current) {
      console.warn(`Ya se está cargando la persona ${id}, ignorando nueva llamada`);
      return;
    }

    // ✅ Verificar si ya tenemos los datos de esta persona
    if (loadedPersonRef.current === id) {
      //console.log(`Persona ${id} ya cargada, evitando request duplicado`);
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);

      //console.log(`🔄 Cargando datos completos de persona ${id} - Request único`);
      const personData = await PeopleService.getPerson(id);

      // ✅ Marcar que ya cargamos esta persona
      loadedPersonRef.current = id;

      // Procesar herramientas si existen
      if (personData.tools && Array.isArray(personData.tools)) {
        personData.tools = personData.tools
          .filter(item => !item.retired)
          .map(item => ({
            id: item.id,
            type: item.tool.type,
            name: item.tool.name,
            serial: item.serial || '',
            quantity: 1,
          }));
      }

      setPerson(personData);
      //console.log(`✅ Persona ${id} cargada exitosamente`);

      // ✅ Cargar imagen de perfil por separado (no bloquear datos principales)
      try {
        //console.log(`🖼️ Cargando imagen de perfil para persona ${id}`);
        const pictureUrl = await PeopleService.getProfilePicture(id);
        setProfilePicture(pictureUrl);
        //console.log(`✅ Imagen de perfil cargada para persona ${id}`);
      } catch (pictureError) {
        //console.log(`ℹ️ No hay imagen de perfil disponible para persona ${id}`);
        setProfilePicture(null);
      }

    } catch (error) {
      console.error(`❌ Error al cargar datos de persona ${id}:`, error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar los datos de la persona',
      });
      
      // ✅ Resetear ref en caso de error
      loadedPersonRef.current = null;
      
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [id]); // ✅ Solo depende del ID

  // ✅ useEffect optimizado - solo ejecuta cuando ID cambia
  useEffect(() => {
    if (id && id !== loadedPersonRef.current) {
      //console.log(`🚀 Nuevo ID detectado: ${id}, iniciando carga completa...`);
      loadPersonData();
    }
  }, [id, loadPersonData]);

  // ✅ Limpiar refs al desmontar
  useEffect(() => {
    return () => {
      loadingRef.current = false;
      loadedPersonRef.current = null;
      //console.log('🧹 PersonProfile desmontado, refs limpiadas');
    };
  }, []);

  const handleEditPerson = () => {
    //console.log(`✏️ Navegando a editar persona ${id}`);
    navigate(`/people/${id}/edit`);
  };

  const handleDeletePerson = async () => {
    if (window.confirm(`¿Está seguro que desea dar de baja a ${person.firstName} ${person.surname}?`)) {
      try {
        //console.log(`🗑️ Eliminando persona ${id}`);
        await PeopleService.deletePerson(id);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Persona dada de baja exitosamente',
        });
        setTimeout(() => navigate('/people'), 1500);
      } catch (error) {
        console.error(`❌ Error al eliminar persona ${id}:`, error);
        
        let errorMessage = 'Error al dar de baja la persona';
        
        // Intentar obtener el mensaje específico del API de diferentes maneras
        try {
          // Opción 1: Si customFetch ya procesó el error y está en error.message
          if (error.message && error.message !== `Error 400: Bad Request`) {
            errorMessage = error.message;
          }
          // Opción 2: Si el error tiene una respuesta con datos
          else if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
          // Opción 3: Intentar hacer una nueva llamada para obtener el error específico
          else if (error.message.includes('400')) {
            // Para errores 400, intentemos hacer un fetch directo para ver la respuesta
            errorMessage = 'La persona tiene equipos asignados. Debe desasignar todos los equipos antes de eliminarla.';
          }
        } catch (parseError) {
          console.error('Error al procesar mensaje de error:', parseError);
          // Mantener mensaje genérico si todo falla
        }
        
        setSnackbar({
          open: true,
          type: 'error',
          message: errorMessage,
        });
      }
    }
  };

  // ✅ NUEVO: Función para restaurar persona
  const handleRestorePerson = async () => {
    if (!person.deletedDate) {
      setSnackbar({
        open: true,
        type: 'warning',
        message: 'Esta persona ya está activa'
      });
      return;
    }

    const displayName = person.type === 'F' 
      ? `${person.firstName} ${person.surname}`.trim()
      : person.businessName || 'Persona jurídica';

    if (window.confirm(`¿Está seguro que desea restaurar a ${displayName}?`)) {
      try {
        const result = await PeopleService.restorePerson(id);
        
        if (result.statusCode >= 200 && result.statusCode < 300) {
          setSnackbar({
            open: true,
            type: 'success',
            message: 'Persona restaurada correctamente'
          });
          
          setTimeout(() => {
            loadedPersonRef.current = null;
            loadPersonData();
          }, 1500);
        } else {
          const errorMessage = result.data?.message || `Error del servidor (${result.statusCode})`;
          
          setSnackbar({
            open: true,
            type: 'error',
            message: errorMessage
          });
        }
      } catch (error) {
        console.error('Error inesperado al restaurar persona:', error);
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error inesperado al restaurar la persona. Intente nuevamente.'
        });
      }
    }
  };

  const handleUploadProfilePicture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 🔒 BLOQUEAR si está dada de baja
    const isPersonDeactivated = person?.deletedDate !== null;
    if (isPersonDeactivated) {
      setSnackbar({
        open: true,
        type: 'warning',
        message: 'No se puede cambiar la foto de una persona dada de baja'
      });
      event.target.value = ''; // Reset file input
      return;
    }

    try {
      //console.log(`📷 Subiendo imagen de perfil para persona ${id}`);
      await PeopleService.uploadProfilePicture(id, file);
      setSnackbar({
        open: true,
        type: 'success',
        message: 'Imagen de perfil actualizada exitosamente',
      });

      // Recargar imagen de perfil
      const pictureUrl = await PeopleService.getProfilePicture(id);
      setProfilePicture(pictureUrl);
      //console.log(`✅ Imagen de perfil actualizada para persona ${id}`);
    } catch (error) {
      console.error(`❌ Error al subir imagen de perfil:`, error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al subir la imagen de perfil',
      });
    }
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  // ✅ Función para recargar datos cuando se actualiza algo
  const handleDataChange = useCallback(() => {
    //console.log(`🔄 Recargando datos de persona ${id} por cambio en sección`);
    // Resetear la referencia para forzar recarga
    loadedPersonRef.current = null;
    loadPersonData();
  }, [id, loadPersonData]);

  // ✅ Función helper para pasar mensajes a secciones hijas
  const handleShowSnackbar = useCallback((type, message) => {
    setSnackbar({
      open: true,
      type,
      message
    });
  }, []);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-2">Cargando datos de la persona...</p>
        </div>
      </Container>
    );
  }

  if (!person) {
    return (
      <Container className="text-center mt-5">
        <h3>No se encontró la persona solicitada</h3>
        <Button variant="primary" onClick={goBack} className="mt-3">
          Volver a la lista
        </Button>
      </Container>
    );
  }

  // ✅ DETECTAR SI LA PERSONA ESTÁ DADA DE BAJA
  const isPersonDeactivated = person?.deletedDate !== null;

  return (
    <Container fluid className="mt-3">
      {/* ✅ ALERTA PARA PERSONAS DADAS DE BAJA */}
      {isPersonDeactivated && (
        <Alert variant="warning" className="mb-3 d-flex align-items-center">
          <FaLock className="me-2" />
          <div>
            <strong>Persona Dada de Baja:</strong> Esta persona fue dada de baja el{' '}
            <strong>{dayjs(person.deletedDate).format(FORMATO_FECHA)}</strong>.
            Solo se pueden editar datos personales.
          </div>
        </Alert>
      )}

      {/* ✅ BOTONES CON LÓGICA CONDICIONAL */}
      <div className="d-flex mb-3">
        <Button variant="outline-secondary" onClick={goBack} className="me-2" size="sm">
          <FaArrowLeft className="me-1" /> Volver
        </Button>
        
        <Button 
          variant="outline-primary" 
          onClick={handleEditPerson} 
          className="me-2" 
          size="sm"
        >
          <FaEdit className="me-1" /> Editar Datos
        </Button>
        
        {/* ✅ BOTÓN CONDICIONAL: Restaurar O Dar de baja */}
        {isPersonDeactivated ? (
          <Button 
            variant="outline-success" 
            onClick={handleRestorePerson} 
            className="me-2"
            size="sm"
          >
            <FaUndo className="me-1" /> Restaurar Persona
          </Button>
        ) : (
          <Button 
            variant="outline-danger" 
            onClick={handleDeletePerson} 
            size="sm"
          >
            <FaUserMinus className="me-1" /> Dar Baja
          </Button>
        )}
        
        {/* ✅ BADGE DE ESTADO */}
        <div className="ms-3">
          {isPersonDeactivated ? (
            <Badge bg="secondary" className="fs-6 px-3 py-2">
              <FaLock className="me-1" /> Dada de Baja
            </Badge>
          ) : (
            <Badge bg="success" className="fs-6 px-3 py-2">
              <FaEye className="me-1" /> Activa
            </Badge>
          )}
        </div>
      </div>

      <Row>
        <Col md={3}>
          <Card>
            <Card.Header>
              <h4 className="mb-0">{person.firstName} {person.secondName}  {person.surname} {person.secondSurname} </h4>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-4 position-relative">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt="Foto de perfil"
                    className="img-fluid profile-picture"
                  />
                ) : (
                  <div className="profile-placeholder">
                    {person.firstName?.charAt(0) || ''}{person.surname?.charAt(0) || ''}
                  </div>
                )}
                <div className="upload-button">
                  <label>
                    <FaCloudUploadAlt size={16} className="me-1" />
                    Examinar...
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadProfilePicture}
                    />
                  </label>
                </div>
              </div>

              <h5 className="border-bottom pb-2 mb-3">Información Personal</h5>

              <InfoRow label="Tipo de Documento" value={person.documentType} />
              <InfoRow label="País" value={getDescripcionPais(person.country, PAISES)} />
              <InfoRow label="Documento" value={person.document} />
              
                <InfoRow label="Género" value={getDescripcionGenero(person.gender, TIPOS_GENERO) || ''} />
              
              
                <InfoRow label="Teléfono" value={person.phone ? person.phone.toString().padStart(9, '0') : ''} />
              
            
                <InfoRow label="Email" value={person.email ? person.email :''} />
              
              
                <InfoRow label="Dirección" value={person.address ? person.address : ''} />


                <InfoRow label="Estado Civil" value={person.civilStatus ? person.civilStatus : ''} />


              <h5 className="border-bottom pb-2 mb-3 mt-4">Información Laboral</h5>

              <InfoRow label="Régimen" value={person.regime} />
              <InfoRow label="Fecha de Ingreso" value={dayjs(person.entryDate).format(FORMATO_FECHA)} />
              
              {/* ✅ MOSTRAR FECHA DE BAJA SI EXISTE */}
              {person.deletedDate && (
                <InfoRow 
                  label="Fecha de Baja" 
                  value={dayjs(person.deletedDate).format(FORMATO_FECHA)} 
                />
              )}

              {person.exitDate !== null ? (
                <InfoRow label="Fecha de Egreso" value={dayjs(person.exitDate).format(FORMATO_FECHA)} />
                ) : (
                <InfoRow label="Fecha de Egreso" value={''} />
                ) }
            </Card.Body>
          </Card>
        </Col>

        <Col md={9}>
          <Card style={{ height: 'calc(100vh - 130px)' }}>
            <Card.Body className="d-flex flex-column" style={{ padding: '1rem' }}>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                <Tab eventKey="documentos" title="Documentos">
                  <DocumentsSection
                    personId={id}
                    documents={person.documents || []}
                    onDataChange={handleDataChange}
                    showSnackbar={handleShowSnackbar}
                    isPersonDeactivated={isPersonDeactivated}
                  />
                </Tab>

                <Tab eventKey="obras" title="Obras Asignadas">
                  <WorksSection
                    personId={id}
                    works={person.works || []}
                    onDataChange={handleDataChange}
                    showSnackbar={handleShowSnackbar}
                    isPersonDeactivated={isPersonDeactivated}
                  />
                </Tab>

                <Tab eventKey="equipos" title="Herramientas Asignadas">
                  <ToolsSection
                    personId={id}
                    tools={person.tools || []}
                    onDataChange={handleDataChange}
                    showSnackbar={handleShowSnackbar}
                    isPersonDeactivated={isPersonDeactivated}
                  />
                </Tab>

                <Tab eventKey="capacitaciones" title="Capacitaciones">
                  <TrainingsSection
                    personId={id}
                    trainings={person.trainings || []}
                    onDataChange={handleDataChange}
                    showSnackbar={handleShowSnackbar}
                    isPersonDeactivated={isPersonDeactivated}
                  />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default PersonProfile;